/**
 * ScenarioRegimeRefsSection -- the regulator's ask a scenario answers.
 *
 * Round 18 R8. The thread's "Cite the regulator's ask" linked to the
 * page it was on and opened nothing: P418 shipped the citation table
 * and nothing could write it. This is the field. Arriving by the
 * thread's link (#regime-refs) puts the cursor in it for a seat that
 * may cite.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ScenarioDetail } from "shared/api/types";
import { useScenarioRegimeRef } from "twin/api/scenario_hooks";

interface Props {
  scenarioId: string;
  detail: ScenarioDetail;
  canEdit: boolean;
}

export function ScenarioRegimeRefsSection({ scenarioId, detail, canEdit }: Props) {
  const { t } = useTranslation();
  const { add, remove } = useScenarioRegimeRef(scenarioId);
  const [citation, setCitation] = useState("");
  const input = useRef<HTMLInputElement | null>(null);
  const refs = Array.isArray(detail.regime_refs) ? detail.regime_refs : [];
  const base = "screens.scenariosView.regimeRefs";

  // Read off the window, not the router, as the basis section does.
  const arrived =
    typeof window !== "undefined" && window.location.hash === "#regime-refs";
  useEffect(() => {
    if (arrived && canEdit) input.current?.focus();
  }, [arrived, canEdit]);

  const failure = add.error ?? remove.error;

  return (
    <section
      id="regime-refs"
      data-testid="scenario-regime-refs"
      className="rounded border border-slate-200 bg-white p-3 text-sm"
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t(`${base}.heading`)}
      </h3>
      {refs.length === 0 ? (
        <p className="mt-2 text-slate-500" data-testid="scenario-regime-refs-none">
          {t(`${base}.none`)}
        </p>
      ) : (
        <ul className="mt-2 space-y-1">
          {refs.map((r) => (
            <li
              key={r.id}
              data-testid="scenario-regime-ref"
              className="flex items-baseline justify-between gap-2"
            >
              <span dir="auto" className="text-slate-800">
                {r.regime_code}: {r.citation}
              </span>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => remove.mutate(r.id)}
                  className="text-xs text-brand-brassDark hover:underline"
                >
                  {t(`${base}.remove`)}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <form
          className="mt-2 flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const value = citation.trim();
            if (!value) return;
            add.mutate(value, { onSuccess: () => setCitation("") });
          }}
        >
          <label className="flex-1 text-xs text-slate-600">
            {t(`${base}.label`)}
            <input
              ref={input}
              data-testid="scenario-regime-ref-input"
              value={citation}
              onChange={(e) => setCitation(e.target.value)}
              placeholder={t(`${base}.placeholder`)}
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
            />
          </label>
          <button
            type="submit"
            data-testid="scenario-regime-ref-add"
            disabled={!citation.trim() || add.isPending}
            className="rounded bg-brand-brass px-3 py-1 text-xs font-medium text-brand-navy disabled:opacity-50"
          >
            {t(`${base}.add`)}
          </button>
        </form>
      )}
      {failure && (
        <p role="alert" className="mt-2 text-xs text-rose-700">
          {t(`${base}.failed`, { reason: failure.message })}
        </p>
      )}
    </section>
  );
}
